import { useEffect, useMemo, useCallback, useState } from "react";
import { useSelector, useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";
import { deactivateUser, getUserList } from "../../../redux/slices/UserSlice";
import { useResetTemporaryPassword } from "../../../hooks/useResetTemporaryPassword";
import { useResetMfa } from "../../../hooks/useResetMfa";
import CustomTable from "../../../components/CustomTable/CustomTable";
import CustomModal from "../../../components/CustomModal/CustomModal";
import CustomCard from "../../../components/CustomCard/CustomCard";
import TableHeader from "../../../components/CustomTable/TableHeader"
import useSearchableTable from "../../../hooks/useSearchableTable";
import { TITLE_TXT, BTN_TXT, MODAL_WIDTHS, DESC_TXT, isRootOrSuperAdmin, isRootUser } from "../../../constants/constants";
import UserForm from "./Components/UserForm";
import { getUserColumns } from "./Components/getUserColumns";
import InfoModal from "../../../components/CustomModal/InfoModal";
import CustomButton from "../../../components/CustomButton/Button";
import DangerButton from "../../../components/CustomButton/DangerButton";
import { Typography, Flex } from "antd";
import { CloseCircleFilled } from "@ant-design/icons";
import {
  TABLE_COLUMN_DEFAULTS,
  buildTableScreenKey,
} from "../../../constants/tableColumnDefaults";
import "./ManageUsers.scss";

const { Text } = Typography;

const ManageUsers = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { userList, loading } = useSelector((state) => state?.user || {});
  const { user } = useSelector((state) => state?.auth || {});
  const resetTemporaryPassword = useResetTemporaryPassword();
  const resetMfa = useResetMfa();
  const canResetMfa = isRootOrSuperAdmin(user);

  const [modal, setModal] = useState({
    type: null, // "addEditUser" | "deactivateWithUsersModal"
    data: null // hold user info
  });
  const [columnControl, setColumnControl] = useState(null);

  useEffect(() => {
    dispatch(getUserList());
  }, [dispatch]);

  const { searchText, setSearchText, filteredData } = useSearchableTable(userList);

  const closeModal = useCallback(() => setModal({ type: null, data: null }), []);

  const handleAddUser = () => setModal({ type: "addEditUser", data: { isEdit: false, user: null } });

  const handleView = useCallback(
    (id) => navigate("/user-role-management/manage-users/user-details", { state: { userId: id } }),
    [navigate]
  );

  const handleEdit = useCallback(
    (id) => {
      const targetUser = userList.find((u) => u.userId === id);
      if (!targetUser || isRootUser(targetUser)) return;
      setModal({ type: "addEditUser", data: { isEdit: true, user: targetUser } });
    },
    [userList]
  );

  const handleDeactivateClick = useCallback((targetUser) => {
    if (isRootUser(targetUser)) return;

    if (targetUser.usersAssignedToRm !== 0) {
      setModal({ type: "deactivateWithUsersModal", data: { user: targetUser } });
    } else {
      setModal({ type: "confirmDeactivate", data: { user: targetUser } });
    }
  }, []);

  const handleConfirmDeactivate = useCallback(async () => {
    const targetUser = modal.data?.user;
    if (!targetUser) return;
    const payload = { userIds: [targetUser.userId] };
    const res = await dispatch(deactivateUser(payload));
    if (res?.payload?.statusCode === 200) {
      closeModal();
      dispatch(getUserList());
    }
  }, [dispatch, modal, closeModal]);

  const handleResetPassword = useCallback(
    (userId) => resetTemporaryPassword({ targetUserId: userId }),
    [resetTemporaryPassword]
  );

  const handleResetMfa = useCallback(
    (userId, userName) => resetMfa(userId, userName),
    [resetMfa]
  );

  const columns = useMemo(
    () =>
      getUserColumns({
        onView: handleView,
        onEdit: handleEdit,
        onDeactivate: handleDeactivateClick,
        onResetPassword: handleResetPassword,
        onResetMfa: handleResetMfa,
        canResetMfa,
      }),
    [
      handleView,
      handleEdit,
      handleDeactivateClick,
      handleResetPassword,
      handleResetMfa,
      canResetMfa,
    ]
  );

  return (
    <CustomCard>
      <TableHeader
        title={TITLE_TXT.USERS_LIST}
        searchText={searchText}
        setSearchText={setSearchText}
        handleBtnOnClick={handleAddUser}
        btnName={BTN_TXT.ADD_USER}
        columnControl={columnControl}
      />

      <CustomTable
        rowKey="userId"
        columns={columns}
        dataSource={filteredData}
        loading={loading}
        screenKey={buildTableScreenKey("manageUsers")}
        defaultColumnKeys={TABLE_COLUMN_DEFAULTS.manageUsers}
        onColumnControlChange={setColumnControl}
      />

      {/* Add/Edit User Modal */}
      {modal.type === "addEditUser" && (
        <CustomModal
          title={modal.data.isEdit ? TITLE_TXT.EDIT_USER : TITLE_TXT.CREATE_USER}
          open
          centered
          width={MODAL_WIDTHS}
          content={
            <UserForm
              isEditMode={modal.data.isEdit}
              editingUser={modal.data.user}
              onClose={closeModal}
            />
          }
        />
      )}
      {modal.type === "deactivateWithUsersModal" && (
        <InfoModal
          title={`Deactivate Failed for ${modal.data?.userName} (${modal.data?.userId})`}
          open
          onCancel={closeModal}
          centered
          content={
            <Flex align="flex-start" gap={15} style={{ marginTop: 20 }}>
              <CloseCircleFilled style={{ fontSize: 36, color: "#EE5353" }} />
              <Text style={{ color: "#464646", fontSize: 14 }}>
                {DESC_TXT.DEACTIVATE_RM}
              </Text>
            </Flex>
          }
        />
      )}
      {modal.type === "confirmDeactivate" && (
        <CustomModal
          title="Deactivate User"
          open
          centered
          onCancel={closeModal}
          width={500}
          content={
            <div>
              <p style={{ fontSize: 14, color: "#333", margin: "16px 0 24px" }}>
                Are you sure you want to deactivate user <strong>{modal.data?.user?.userName}</strong> ({modal.data?.user?.userId})?
              </p>
              <Flex justify="flex-end" gap={12} style={{ paddingBottom: 16 }}>
                <CustomButton onClick={closeModal}>
                  Cancel
                </CustomButton>
                <DangerButton isSolid onClick={handleConfirmDeactivate}>
                  Deactivate
                </DangerButton>
              </Flex>
            </div>
          }
        />
      )}
    </CustomCard>
  );
};

export default ManageUsers;
